// Authentication service using the Swagger API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
import { useAccountStore, Account } from '@/store/accountStore';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    username: string;
    pk: string;
    domains: string[];
    first_name: string;
    last_name: string;
    is_active: boolean | null;
    tfa_enabled: boolean | null;
  }
  activeSignature: {
    signature_id: string;
    user_id: string;
    signature: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    user: {
      username: string;
      first_name: string;
      last_name: string;
    }
  }
}

export interface ApiError {
  message: string;
  status: number;
}

export interface UpdatePasswordRequest {
  userId: string;
  password: string;
}

export interface UpdateProfileRequest {
  first_name: string;
  last_name: string;
}

class AuthService {
  API_BASE_URL: string;

  constructor() {
    this.API_BASE_URL = API_BASE_URL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAccessToken();
    const url = `${this.API_BASE_URL}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, {
        ...defaultOptions,
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw {
          message: errorData.message || `HTTP error! status: ${response.status}`,
          status: response.status,
        } as ApiError;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  async login(credentials: LoginCredentials, keepExistingSession: boolean = false): Promise<LoginResponse> {
    try {
      const response = await this.makeRequest<LoginResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });

      if (response.accessToken) {
        const accountStore = useAccountStore.getState();
        const newAccount: Account = {
          id: response.user.id,
          username: response.user.username,
          first_name: response.user.first_name,
          last_name: response.user.last_name,
          domains: response.user.domains,
          token: response.accessToken,
          isActive: !keepExistingSession
        };

        // Add the new account to the store
        accountStore.addAccount(newAccount);

        // If we're not keeping existing session, make this the active account
        if (!keepExistingSession) {
          accountStore.setActiveAccount(newAccount.id);
          // Clear existing session data
          localStorage.clear();
          // Set new session data
          localStorage.setItem('accessToken', response.accessToken);
          localStorage.setItem('refreshToken', response.refreshToken);
          localStorage.setItem('user', JSON.stringify(response.user));
        }
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async logout(accountId?: string) {
    try {
      await this.makeRequest('/api/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // Ignore logout errors
    } finally {
      const accountStore = useAccountStore.getState();
      
      if (accountId) {
        // Remove specific account
        accountStore.removeAccount(accountId);
        
        // If this was the active account, switch to another account if available
        if (accountStore.activeAccountId === accountId) {
          const remainingAccount = accountStore.accounts[0];
          if (remainingAccount) {
            accountStore.setActiveAccount(remainingAccount.id);
            localStorage.setItem('accessToken', remainingAccount.token);
            localStorage.setItem('user', JSON.stringify({
              id: remainingAccount.id,
              username: remainingAccount.username,
              first_name: remainingAccount.first_name,
              last_name: remainingAccount.last_name,
              domains: remainingAccount.domains
            }));
          } else {
            // No accounts left, clear everything
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
          }
        }
      } else {
        // Logout from all accounts
        accountStore.accounts.forEach(account => {
          accountStore.removeAccount(account.id);
        });
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    }
  }

  async refreshToken(): Promise<LoginResponse> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await this.makeRequest<LoginResponse>('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });

      if (response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async updatePassword(data: UpdatePasswordRequest): Promise<void> {
    try {
      await this.makeRequest('/api/auth/update-password', {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw error;
    }
  }

  async updateProfile(userId: string, data: UpdateProfileRequest): Promise<void> {
    try {
      await this.makeRequest(`/api/auth/update-profile/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });

      // Update local storage user data
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          first_name: data.first_name,
          last_name: data.last_name,
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      throw error;
    }
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  // Method to test API connectivity
  async testConnection(): Promise<boolean> {
    try {
      await fetch(`${API_BASE_URL}`, { method: 'GET' });
      return true;
    } catch (error) {
      return false;
    }
  }
}

export const authService = new AuthService(); 